import { motion } from "framer-motion";
import { ArrowDownRight, Phone, Sparkles } from "lucide-react";
import { BRAND } from "@/lib/brand";
import Hero3DVisual from "@/components/Hero3DVisual";

export default function Hero3D() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 md:pt-40 pb-24 overflow-hidden"
    >
      <div className="hidden lg:block absolute left-6 top-1/2 -translate-y-1/2 z-10">
        <span className="vert overline text-[#0B1B3D]/50">
          AMFI · IRDAI · QPFP CERTIFIED
        </span>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 grid lg:grid-cols-12 gap-10 items-center">
        {/* LEFT — copy */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-7 relative z-10"
        >
          <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full border border-[#0B1B3D]/15 bg-white/60">
            <Sparkles className="w-3.5 h-3.5 text-[#00A65A]" />
            <span className="text-xs font-medium text-[#0B1B3D]">
              Trusted by 100+ Indian families · Since 2024
            </span>
          </div>

          <h1
            data-testid="hero-headline"
            className="font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight leading-[1.02] text-[#0B1B3D]"
          >
            Your partner in
            <br />
            <span className="italic font-light text-[#00A65A]">wealth</span>
            <span className="text-[#0B1B3D]"> creation.</span>
          </h1>

          <p className="mt-7 max-w-xl text-lg md:text-xl text-[#0B1B3D]/70 leading-relaxed font-body">
            Disciplined investing, goal-driven planning and 18 years of conviction.
            Akshar Investments helps Indian families compound capital with clarity —
            from your first SIP to a fully funded retirement.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-4">
            <a
              href="#contact"
              data-testid="hero-primary-cta"
              className="cta-glow inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#0B1B3D] text-white font-medium hover:bg-[#1A2F5B] transition-colors"
            >
              Book a free consultation
              <ArrowDownRight className="w-4 h-4" />
            </a>
            <a
              href={BRAND.phoneHref}
              data-testid="hero-call-cta"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-transparent text-[#0B1B3D] font-medium border border-[#0B1B3D]/25 hover:border-[#0B1B3D] hover:bg-[#0B1B3D]/5 transition-all"
            >
              <Phone className="w-4 h-4 text-[#00A65A]" />
              {BRAND.phone}
            </a>
          </div>

          <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
            {[
              { v: "18+", l: "Years experience" },
              { v: "100+", l: "Families served" },
              { v: "QPFP", l: "Certified" },
            ].map((k) => (
              <div key={k.l} className="border-l-2 border-[#00A65A] pl-3">
                <div className="big-number text-3xl md:text-4xl text-[#0B1B3D]">{k.v}</div>
                <div className="overline text-[10px] mt-1">{k.l}</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* RIGHT — 3D Canvas (lazy + error-boundaried) */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="lg:col-span-5 relative h-[420px] md:h-[520px] lg:h-[600px]"
        >
          <div
            aria-hidden
            className="absolute inset-0 rounded-[40px] -z-0"
            style={{
              background:
                "radial-gradient(circle at 60% 40%, rgba(16,185,129,0.18), transparent 60%), radial-gradient(circle at 30% 70%, rgba(11,27,61,0.08), transparent 60%)",
            }}
          />

          <Hero3DVisual />

          <div className="absolute bottom-6 left-6 bg-white/85 backdrop-blur-md border border-black/5 rounded-2xl px-4 py-3 shadow-xl z-10">
            <div className="overline text-[10px] text-[#00A65A]">Live since</div>
            <div className="font-display text-2xl text-[#0B1B3D]">2024</div>
          </div>
          <div className="absolute top-6 right-6 bg-[#0B1B3D] text-white rounded-2xl px-4 py-3 shadow-xl z-10">
            <div className="overline text-[10px] text-[#10B981]">SIP Power</div>
            <div className="big-number text-2xl">₹10K → ₹1Cr</div>
            <div className="text-[10px] opacity-70 mt-0.5">at 12% over 25 yrs</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
